import { APIHandler, EventSourceHandler } from "../../base";
import { KnowledgeBaseRequest } from "../../../common/interfaces/admin/knowledge-base-request.interface";
import { KBConfigRequest } from "../../../common/interfaces/admin/knowledge-base-config-request.interface";
import { FilesMetadata } from "../../../common/interfaces/admin/file-metadata.interface";

export const fetchKnowledgeBases = (companyId: number) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases?pageSize=1000`);

// Assistants list (assistants_agents_separation_design.md §4): rows enriched
// with document count, processing basics, and the serving agent. Hidden
// Assistants ride along (admins/managers only server-side) — the view's
// Type filter decides whether they show.
export const fetchKnowledgeBasesSummary = (companyId: number) =>
    APIHandler.get(
        `/admin/${companyId}/knowledgeBases?pageSize=1000&include=summary&includeHidden=true`
    );

export const fetchKnowledgeBase = (companyId: number, kbUid: string) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/${kbUid}`);

export const patchKnowledgeBase = (companyId: number, kbUid: string, data: KnowledgeBaseRequest) =>
    APIHandler.patch(`/admin/${companyId}/knowledgeBases/${kbUid}`, data);

export const createKnowledgeBase = (companyId: number, name: string) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${name}`);

export const deleteKnowledgeBase = (companyId: number, kbUid: string) =>
    APIHandler.delete(`/admin/${companyId}/knowledgeBases/${kbUid}`);

export const fetchKnowledgeBaseConfig = (companyId: number, kbUid: string) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/${kbUid}/config`);

export const patchKnowledgeBaseConfig = (companyId: number, kbUid: string, data: KBConfigRequest) =>
    APIHandler.patch(`/admin/${companyId}/knowledgeBases/${kbUid}/config`, data);

export const fetchKBLogoUrl = (companyId: number, kbUid: string, fileData: FilesMetadata) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/logo`, { file: fileData });

export const forceUpdate = (companyId: number, kbUid: string) =>
    APIHandler.put(`/admin/${companyId}/knowledgeBases/${kbUid}/forceUpdate`);

export const subscribeForceUpdate = (companyId: number) =>
    EventSourceHandler(`/admin/${companyId}/sse`);

export const reindexKnowledgeBase = (companyId: number, kbUid: string) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/reindex`);

export const reindexLearningObject = (companyId: number, kbUid: string, learningObjectId: string) =>
    APIHandler.post(
        `/admin/${companyId}/knowledgeBases/${kbUid}/learningObjects/${learningObjectId}/reindex`
    );

export const forceFileProcessing = (
    companyId: number,
    kbUid: string,
    filters: { contentItemProviderId?: number; courseId?: string; learningObjectId?: string },
    accessToken?: { oneAccessToken?: string; googleAccessToken?: string },
    resetOptions?: { resetVideoTimeline?: boolean; resetVideoTranscript?: boolean }
) =>
    APIHandler.put(
        `/admin/${companyId}/knowledgeBases/${kbUid}/forceFileProccessing`,
        {
            isRecognize: true,
            isGenerateSummary: false,
            ...accessToken,
            ...resetOptions
        },
        { params: filters }
    );

export const fetchDigestionPolicyTemplates = (companyId: number) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/digestion-policy-templates`);

export const saveDigestionPolicyTemplate = (
    companyId: number,
    data: { name: string; description?: string; fields: unknown[]; overwrite?: boolean }
) => APIHandler.post(`/admin/${companyId}/knowledgeBases/digestion-policy-templates`, data);

export const deleteDigestionPolicyTemplate = (companyId: number, templateUid: string) =>
    APIHandler.delete(
        `/admin/${companyId}/knowledgeBases/digestion-policy-templates/${templateUid}`
    );

export const promoteDigestionPolicyTemplate = (
    companyId: number,
    templateUid: string,
    overwrite?: boolean
) =>
    APIHandler.post(
        `/admin/${companyId}/knowledgeBases/digestion-policy-templates/${templateUid}/promote`,
        { overwrite }
    );

export const proposeDigestionPolicy = (companyId: number, kbUid: string) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/config/propose-digestion-policy`);

export const reextractMetadata = (companyId: number, kbUid: string) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/config/reextract-metadata`);

export const rechunkKnowledgeBase = (companyId: number, kbUid: string) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/config/rechunk`);

export const redigestLegacy = (companyId: number, kbUid: string, limit?: number) =>
    APIHandler.post(`/admin/${companyId}/knowledgeBases/${kbUid}/config/redigest-legacy`, {
        limit
    });

export const fetchExtractedMetadata = (companyId: number, kbUid: string) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/${kbUid}/config/extracted-metadata`);

export const fetchKnowledgeBaseInvites = (companyId: number, kbUid: string) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/${kbUid}/invites?pageSize=100`);

export const fetchRecognitionChunks = (
    companyId: number,
    kbUid: string,
    recognitionResultId: string
) =>
    APIHandler.get(
        `/admin/${companyId}/knowledgeBases/${kbUid}/recognitionResults/${recognitionResultId}/chunks`
    );
